"use client"
import type React from "react"
import { useEffect, useState } from "react"
import { Card, CardBody, Badge, Input, Row, Col, FormGroup, Label } from "reactstrap"
import DataTable from "react-data-table-component"
import { Btn } from "../../../AbstractElements"
import ExhibitorsService from "../../../Service/ExhibitorsService"
import type { Exhibitors } from "./types"
import Swal from "sweetalert2"
import { FaPlus, FaEdit, FaTrash, FaEye, FaFileAlt, FaTimes } from "react-icons/fa"
import { useNavigate } from "react-router-dom"

interface Props {
  onAdd: () => void
  onEdit: (item: Exhibitors) => void
  refresh: boolean
}

const ExhibitorsList: React.FC<Props> = ({ onAdd, onEdit, refresh }) => {
  const [data, setData] = useState<Exhibitors[]>([])
  const [loading, setLoading] = useState(false)
  const [totalRows, setTotalRows] = useState(0)
  const [perPage, setPerPage] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)

  // ✅ All search parameters - sent as single search string
  const [textSearch, setTextSearch] = useState("") // Regular text search
  const [emailSentSearch, setEmailSentSearch] = useState("") // "" | "email_sent:true" | "email_sent:false"
  const [postpaidSearch, setPostpaidSearch] = useState("") // "" | "postpaid:true" | "postpaid:false"

  const navigate = useNavigate()

  // ✅ Combine all search parameters into one search string
  const buildSearchString = () => {
    const searchParts = []

    if (textSearch.trim()) {
      searchParts.push(textSearch.trim())
    }
    if (emailSentSearch) {
      searchParts.push(emailSentSearch)
    }
    if (postpaidSearch) {
      searchParts.push(postpaidSearch)
    }

    return searchParts.join(" ")
  }

  const fetchData = async (page = 1, size = perPage) => {
    setLoading(true)
    try {
      const searchString = buildSearchString()
      const params: any = {
        page,
        page_size: size,
        search: searchString, // ✅ Single search parameter
      }

      console.log("🔍 Search string:", searchString)

      const response = await ExhibitorsService.getAll(params)
      setData(response.results)
      setTotalRows(response.count)
      setCurrentPage(response.current_page)
    } catch {
      Swal.fire("Error", "Failed to load exhibitors.", "error")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData(1, perPage)
  }, [refresh, textSearch, emailSentSearch, postpaidSearch])

  // Handle search with debounce (only for text search)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchData(1, perPage)
    }, 500)
    return () => clearTimeout(timeoutId)
  }, [textSearch])

  // Handle dropdown changes immediately (no debounce)
  useEffect(() => {
    fetchData(1, perPage)
  }, [emailSentSearch, postpaidSearch])

  const handlePageChange = (page: number) => {
    fetchData(page, perPage)
  }

  const handlePerRowsChange = async (newPerPage: number, page: number) => {
    setPerPage(newPerPage)
    fetchData(page, newPerPage)
  }

  // ✅ Clear all search parameters
  const clearAllSearch = () => {
    setTextSearch("")
    setEmailSentSearch("")
    setPostpaidSearch("")
  }

  // ✅ Check if any search is active
  const hasActiveSearch = () => {
    return textSearch.trim() !== "" || emailSentSearch !== "" || postpaidSearch !== ""
  }

  const handleViewDetails = (exhibitor: Exhibitors) => {
    navigate(`${process.env.PUBLIC_URL}/exhibitor/${exhibitor.id}/related`)
  }

  const handleViewDocument = async (exhibitor: Exhibitors) => {
    if (!exhibitor.postpaid_document) {
      Swal.fire("Error", "No document available to view.", "error")
      return
    }
    try {
      await ExhibitorsService.viewDocument(exhibitor.postpaid_document)
    } catch (error) {
      Swal.fire("Error", "Failed to open document.", "error")
    }
  }

  const handleDelete = async (exhibitor: Exhibitors) => {
    const result = await Swal.fire({
      title: "Delete Exhibitor?",
      text: `This will permanently remove "${exhibitor.name}".`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
      reverseButtons: true,
      focusCancel: true,
    })
    if (!result.isConfirmed) return

    try {
      await ExhibitorsService.delete(exhibitor.id)
      await Swal.fire({
        title: "Deleted!",
        text: "Exhibitor has been removed.",
        icon: "success",
        timer: 2000,
        timerProgressBar: true,
      })
      fetchData(currentPage, perPage)
    } catch {
      Swal.fire("Error", "Failed to delete exhibitor.", "error")
    }
  }

  const columns = [
    {
      name: "Name / TIN",
      cell: (r: any) => (
        <div className="py-2 exhibitor-name-cell">
          <div className="fw-semibold text-dark">{r.name}</div>
          <div className="text-muted small mt-1">TIN: {r.tin}</div>
          <div className="small mt-1">
            {r.email_sent ? (
              <span className="text-success d-inline-flex align-items-center">
                <i className="fa fa-check-circle me-1"></i>
                Emailed
              </span>
            ) : (
              <span className="text-danger d-inline-flex align-items-center">
                <i className="fa fa-times-circle me-1"></i>
                Not emailed
              </span>
            )}
          </div>
        </div>
      ),
      sortable: true,
      wrap: true,
      grow: 2,
      minWidth: "220px",
    },
    {
      name: "Email / Phone",
      cell: (r: Exhibitors) => (
        <div className="py-2">
          <div className="text-break">{r.email}</div>
          <div className="text-muted">{r.phone}</div>
        </div>
      ),
      wrap: true,
      grow: 2,
      minWidth: "200px",
    },
    {
      name: "Country",
      cell: (r: Exhibitors) => <span className="fw-medium">{r.country_name || "No Country"}</span>,
      wrap: true,
      minWidth: "140px",
    },
    {
      name: "Postpaid",
      cell: (r: Exhibitors) => (
        <Badge color={r.postpaid ? "info" : "secondary"} pill>
          {r.postpaid ? "Yes" : "No"}
        </Badge>
      ),
      sortable: true,
      center: true,
      minWidth: "110px",
    },
    {
      name: "Host",
      cell: (r: Exhibitors) => (
        <Badge color={r.is_host ? "primary" : "secondary"} pill>
          {r.is_host ? "Yes" : "No"}
        </Badge>
      ),
      sortable: true,
      center: true,
      minWidth: "100px",
    },
    {
      name: "Status",
      cell: (r: Exhibitors) => (
        <Badge color={r.status ? "success" : "secondary"} pill>
          {r.status ? "Active" : "Inactive"}
        </Badge>
      ),
      sortable: true,
      center: true,
      minWidth: "110px",
    },
    {
      name: "Actions",
      cell: (row: Exhibitors) => (
        <div className="action-toolbar">
          <button className="action-btn action-btn-edit" onClick={() => onEdit(row)} title="Edit">
            <FaEdit size={13} />
          </button>
          <button className="action-btn action-btn-view" onClick={() => handleViewDetails(row)} title="View Details">
            <FaEye size={13} />
          </button>
          {row.postpaid && row.postpaid_document && (
            <button
              className="action-btn action-btn-doc"
              onClick={() => handleViewDocument(row)}
              title="View Postpaid Document"
            >
              <FaFileAlt size={13} />
            </button>
          )}
          {row.postpaid && !row.postpaid_document && (
            <button className="action-btn action-btn-disabled" disabled title="No Document">
              <FaFileAlt size={13} />
            </button>
          )}
          <button className="action-btn action-btn-delete" onClick={() => handleDelete(row)} title="Delete">
            <FaTrash size={13} />
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      minWidth: "180px",
    },
  ]

  const customStyles = {
    table: {
      style: {
        overflow: "visible",
      },
    },
    tableWrapper: {
      style: {
        overflow: "visible",
      },
    },
    headRow: {
      style: {
        backgroundColor: "#f8f9fa",
        borderBottom: "2px solid #e9ecef",
        minHeight: "48px",
      },
    },
    headCells: {
      style: {
        fontSize: "0.78rem",
        fontWeight: 700,
        textTransform: "uppercase" as const,
        letterSpacing: "0.03em",
        color: "#6c757d",
      },
    },
    rows: {
      style: {
        minHeight: "72px",
      },
    },
    cells: {
      style: {
        paddingTop: "6px",
        paddingBottom: "6px",
        whiteSpace: "normal" as const,
      },
    },
  }

  return (
    <Card>
      <CardBody>
        {/* ✅ Search Row - Text + Dropdown Searches */}
        <Row className="mb-3 align-items-end">
          <Col md={4}>
            <FormGroup>
              <Label for="textSearch">Search Exhibitors</Label>
              <Input
                type="search"
                id="textSearch"
                placeholder="Search by name, email, phone, TIN..."
                value={textSearch}
                onChange={(e) => setTextSearch(e.target.value)}
              />
            </FormGroup>
          </Col>

          <Col md={3}>
            <FormGroup>
              <Label for="emailSentSearch">Email Status</Label>
              <Input
                type="select"
                id="emailSentSearch"
                value={emailSentSearch}
                onChange={(e) => setEmailSentSearch(e.target.value)}
              >
                <option value="">All Email Status</option>
                <option value="email_sent:true">Email Sent</option>
                <option value="email_sent:false">Email Not Sent</option>
              </Input>
            </FormGroup>
          </Col>

          <Col md={3}>
            <FormGroup>
              <Label for="postpaidSearch">Payment Type</Label>
              <Input
                type="select"
                id="postpaidSearch"
                value={postpaidSearch}
                onChange={(e) => setPostpaidSearch(e.target.value)}
              >
                <option value="">All Payment Types</option>
                <option value="postpaid:true">Postpaid</option>
                <option value="postpaid:false">Prepaid</option>
              </Input>
            </FormGroup>
          </Col>

          <Col md={2}>
            <FormGroup>
              <div className="d-flex gap-2">
                {/* Clear Search Button */}
                {hasActiveSearch() && (
                  <Btn color="outline-secondary" onClick={clearAllSearch} title="Clear all search">
                    <FaTimes size={12} />
                  </Btn>
                )}
                {/* Add Exhibitor Button */}
                <Btn color="primary" onClick={onAdd}>
                  <FaPlus className="me-1" size={12} /> Add
                </Btn>
              </div>
            </FormGroup>
          </Col>
        </Row>

        {/* ✅ Active Search Display */}
        {hasActiveSearch() && (
          <Row className="mb-3">
            <Col>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <small className="text-muted">Searching for:</small>

                {textSearch.trim() && (
                  <Badge color="primary" className="d-flex align-items-center">
                    Text: "{textSearch.trim()}"
                    <FaTimes
                      className="ms-1"
                      size={10}
                      onClick={() => setTextSearch("")}
                      style={{ cursor: "pointer" }}
                      title="Clear text search"
                    />
                  </Badge>
                )}

                {emailSentSearch && (
                  <Badge
                    color={emailSentSearch === "email_sent:true" ? "success" : "danger"}
                    className="d-flex align-items-center"
                  >
                    Email: {emailSentSearch === "email_sent:true" ? "Sent" : "Not Sent"}
                    <FaTimes
                      className="ms-1"
                      size={10}
                      onClick={() => setEmailSentSearch("")}
                      style={{ cursor: "pointer" }}
                      title="Clear email search"
                    />
                  </Badge>
                )}

                {postpaidSearch && (
                  <Badge
                    color={postpaidSearch === "postpaid:true" ? "info" : "warning"}
                    className="d-flex align-items-center"
                  >
                    Payment: {postpaidSearch === "postpaid:true" ? "Postpaid" : "Prepaid"}
                    <FaTimes
                      className="ms-1"
                      size={10}
                      onClick={() => setPostpaidSearch("")}
                      style={{ cursor: "pointer" }}
                      title="Clear payment search"
                    />
                  </Badge>
                )}

                <small className="text-muted">(Search string: "{buildSearchString()}")</small>
              </div>
            </Col>
          </Row>
        )}

        <div className="exhibitors-table-wrapper">
          <DataTable
            columns={columns}
            data={data}
            customStyles={customStyles}
            progressPending={loading}
            pagination
            paginationServer
            paginationTotalRows={totalRows}
            paginationDefaultPage={currentPage}
            paginationPerPage={perPage}
            paginationRowsPerPageOptions={[10, 20, 30, 50]}
            onChangeRowsPerPage={handlePerRowsChange}
            onChangePage={handlePageChange}
            highlightOnHover
            striped
            noDataComponent="No exhibitors found."
          />
        </div>
      </CardBody>
      <style>{`
        .exhibitors-table-wrapper {
          overflow-x: auto;
        }

        .exhibitor-name-cell {
          line-height: 1.35;
        }

        .action-toolbar {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-wrap: nowrap;
        }

        .action-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 30px;
          height: 30px;
          border-radius: 6px;
          border: 1px solid transparent;
          background-color: #f1f3f5;
          color: #495057;
          transition: all 0.15s ease;
          cursor: pointer;
        }

        .action-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
        }

        .action-btn-edit:hover { background-color: #e7f1ff; color: #0d6efd; }
        .action-btn-view:hover { background-color: #e6f8ee; color: #198754; }
        .action-btn-doc:hover { background-color: #e7f1ff; color: #0d6efd; }
        .action-btn-delete:hover { background-color: #fdeaea; color: #dc3545; }

        .action-btn-disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }

        .action-btn:focus-visible {
          outline: 2px solid #0d6efd;
          outline-offset: 1px;
        }
      `}</style>
    </Card>
  )
}

export default ExhibitorsList